import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Heading } from "../common/Heading/Heading";
import { Button } from "../common/Button/Button";
import { LoadingIndicator } from "../common/LoadingIndicator/LoadingIndicator";
import { TestConversationMessages } from "./TestConversationMessages/TestConversationMessages";
import { AnswerDetailsView } from "../common/AnswerDetailsView/AnswerDetailsView";
import { useLogs } from "../../context/LogsContext";
import { TitleSection, ButtonContainer } from "./Test.styles";
import axios from "../../api/axiosInstance";

// Chat service function
const getAnswerResponse = async (messages) => {
  const chat_id = `test-${Math.floor(100000 + Math.random() * 900000)}`;

  const { data } = await axios.post(
    `${process.env.REACT_APP_API_URL}/chat/answer`,
    {
      messages,
      chat_id,
    }
  );
  return data;
};

export const Test = () => {
  const { t, i18n } = useTranslation();
  const [conversationData, setConversationData] = useState([]);
  const [summary, setSummary] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [sources, setSources] = useState([]);
  const [errorInfo, setErrorInfo] = useState(null);
  const [successStatus, setSuccessStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState("");
  const [error, setError] = useState(null);
  const [answerId, setAnswerId] = useState(null);
  const { fetchAnswerLogs, answerLogsMap, isLoadingAnswerLogs } = useLogs();

  // Get the current log data
  const getLogsData = () => {
    if (
      !answerId ||
      !answerLogsMap[answerId] ||
      !answerLogsMap[answerId].logs ||
      !Array.isArray(answerLogsMap[answerId].logs) ||
      answerLogsMap[answerId].logs.length === 0
    ) {
      return [];
    }

    // Map of step names to their display order
    const stepOrder = {
      getSummaryResponse: 0,
      validateQuestionSimilarity: 1,
      filterSections: 2,
      generateAnswerFromSections: 3,
    };

    // Sort logs by their step order
    return [...answerLogsMap[answerId].logs].sort((a, b) => {
      const aOrder = stepOrder[a.step] !== undefined ? stepOrder[a.step] : 999;
      const bOrder = stepOrder[b.step] !== undefined ? stepOrder[b.step] : 999;
      return aOrder - bOrder;
    });
  };

  // Process logs data to extract information
  const processLogsData = (response) => {
    try {
      if (response) {
        // Extract data directly from the response object
        setSummary(response.summary || "");
        setQuestion(response.question || "");
        setAnswer(response.answer || "");
        setSources(response.sources || []);
        setErrorInfo(response.error || null);
        setSuccessStatus(response.success_status || "");

        // Extract conversation data from the first log if available
        if (response.logs && response.logs.length > 0) {
          const firstLog = response.logs[0];
          if (firstLog && firstLog.prompt) {
            try {
              const promptData = JSON.parse(firstLog.prompt);
              if (
                promptData &&
                promptData.messages &&
                promptData.messages.length > 1
              ) {
                const messages = JSON.parse(promptData.messages[1].content);
                setConversationData(messages || []);
              }
            } catch (err) {
              console.error("Error processing messages:", err);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error processing response data:", err);
      // Don't set error here, we want to display partial data if available
    }
  };

  // Clear all data
  const handleClearAll = () => {
    if (isLoading || isLoadingAnswerLogs) return;

    setConversationData([]);
    setSummary("");
    setQuestion("");
    setAnswer("");
    setSources([]);
    setErrorInfo(null);
    setSuccessStatus("");
    setError(null);
    setAnswerId(null);
    setLoadingStage("");
  };

  // Generate answer from API and fetch logs
  const handleGenerateAnswer = async () => {
    if (conversationData.length === 0) {
      setError(t("test.noMessagesToProcess"));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setErrorInfo(null);
      setSuccessStatus("");
      setLoadingStage("generating");

      let response;
      let responseAnswerId = null;

      try {
        response = await getAnswerResponse(conversationData);

        // Get answer_id from response if available
        if (response && response.answer_id) {
          responseAnswerId = response.answer_id;
          setAnswerId(responseAnswerId);
        }
      } catch (err) {
        console.error("Error in answer generation:", err);
        // Don't set error yet, we might still have an answer_id
        if (err.response && err.response.data && err.response.data.answer_id) {
          responseAnswerId = err.response.data.answer_id;
          setAnswerId(responseAnswerId);
        } else {
          // Only set error if we couldn't get an answer_id
          setError(t("errors.failedToGetAnswerId"));
        }
      }

      // If we have an answer_id, fetch logs regardless of errors
      if (responseAnswerId) {
        setLoadingStage("fetchingLogs");

        try {
          const logsResponse = await fetchAnswerLogs(responseAnswerId);

          // Process response data to extract information
          processLogsData(logsResponse);
          setError(null); // Clear any previous errors since we got logs
        } catch (logErr) {
          console.error("Error fetching logs:", logErr);
          setError(t("errors.failedToFetchAnswerLogs"));
        }
      } else {
        // If we don't have an answer_id at all, show error
        setError(t("errors.failedToGetAnswerId"));
      }
    } finally {
      setIsLoading(false);
      setLoadingStage("");
    }
  };

  const getLoadingMessage = () => {
    if (loadingStage === "generating") {
      return t("test.generatingAnswer");
    } else if (loadingStage === "fetchingLogs") {
      return t("test.fetchingLogs");
    } else if (isLoadingAnswerLogs) {
      return t("test.fetchingLogs");
    }
    return t("common.loading");
  };

  const conversationComponent = (
    <TestConversationMessages
      conversationData={conversationData}
      setConversationData={setConversationData}
      t={t}
      disableEditing={isLoading || isLoadingAnswerLogs}
    />
  );

  const headerContent = (
    <>
      <TitleSection>
        <Heading
          text={i18n.language === "ru" ? "Тестовая страница" : "Test Page"}
          size={1}
        />
        <ButtonContainer>
          <Button
            text={t("test.generateAnswer")}
            onClick={handleGenerateAnswer}
            variant="violet"
            disabled={
              isLoading || isLoadingAnswerLogs || conversationData.length === 0
            }
          />
          <Button
            text={t("test.clearConversation")}
            onClick={handleClearAll}
            variant="white"
            disabled={
              isLoading || isLoadingAnswerLogs || conversationData.length === 0
            }
          />
        </ButtonContainer>
      </TitleSection>

      {answerId && !isLoading && !isLoadingAnswerLogs && (
        <AnswerIdContainer>
          {t("logs.answerId")}: {answerId}
        </AnswerIdContainer>
      )}

      {(isLoading || isLoadingAnswerLogs) && (
        <LoadingIndicator message={getLoadingMessage()} />
      )}
    </>
  );

  // Only show the error if defined, but keep the UI visible
  const displayError = error ? error : null;

  return (
    <AnswerDetailsView
      conversationComponent={conversationComponent}
      summary={summary}
      question={question}
      answer={answer}
      sources={sources}
      logsData={getLogsData()}
      isLoading={false} // Never set isLoading true here to keep the UI visible
      loadingMessage={getLoadingMessage()}
      error={displayError}
      errorInfo={errorInfo}
      successStatus={successStatus}
      children={headerContent}
    />
  );
};

const AnswerIdContainer = styled.div`
  margin-bottom: 1rem;
`;
